import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../supabase';
import { stateName } from '../constants/usStates';
import { parseAssessorTemplate } from '../utils/assessorTemplate';
import { entityKey } from '../utils/entityKey';
import { pageAll } from '../utils/pageAll';

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// TWO-TRACK Assessor/Collector importer — the live Import on the Assessors & Collectors page, for the
// a74e710 schema. Reads the "Assessors Screen" and "Collectors" tabs of the state upload template.
// Parsing (header-driven layout, installment blocks, date/percent rules) lives in utils/assessorTemplate.js,
// pure so scripts/check-assessor-templates.mjs can run every state file through the same code.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

// ── shared helpers (carried over from the single-track importer) ──────────────────────────────
const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };

// Group rows by their year column FIRST, then size-chunk within each year, so every statement this
// importer issues touches exactly ONE year. Required by the cross-year isolation triggers (a statement
// spanning years is rejected); also the difference between "2023 failed" and "chunk 7 of 19 failed".
// Yields [year, rows] pairs. A row with a null/undefined year groups under '' and will fail loudly at
// insert rather than being silently dropped here.
const byYearChunks = (rows, yearCol, n) => {
  const byYear = new Map();
  rows.forEach(r => { const y = r[yearCol] ?? ''; if (!byYear.has(y)) byYear.set(y, []); byYear.get(y).push(r); });
  const out = [];
  for (const [y, rs] of byYear) for (const part of chunk(rs, n)) out.push([y, part]);
  return out;
};

// PRE-FLIGHT: refuse a file whose rows can't be written one-year-at-a-time before ANY write happens.
// This loop has no transaction, so a mid-batch rejection leaves earlier chunks committed — the check has
// to run first. Returns an error string, or null when the set is safe.
const preflightYears = (label, rows, yearCol) => {
  const missing = rows.filter(r => r[yearCol] === null || r[yearCol] === undefined || r[yearCol] === '').length;
  if (missing) return `${missing} ${label} row(s) have no ${yearCol}. Every row must carry its year — nothing was imported.`;
  return null;
};

// CORE-name key lives in utils/entityKey — shared with the SYNC importer and the Add/Edit modals so a
// hand-added office resolves to the same entity_id an import would find (never a split entity).
const nkey = (s) => entityKey(s);
// Paginate the state-scoped entity match-index past the 1000-row cap. collectors/assessors are per-year
// versioned (~1816 active rows); IL+FL+CA alone exceeds 1000, so a bare .in('state', …) would SILENTLY
// truncate the index → existing entities past the alphabetical cut get re-minted as DUPLICATES on import.
async function loadEntitiesByStates(table, states) {
  const out = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from(table)
      .select('entity_id,name,state').in('state', states).range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return out;
}
const sigOf = (fields) => { const rest = { ...(fields || {}) }; delete rest.name; return JSON.stringify(Object.keys(rest).sort().map(k => [k, rest[k] == null ? null : String(rest[k])])); };
const instSig = (installments) => JSON.stringify((installments || []).map(r => `${r.kind}|${r.bill_type}|${r.split_pct}|${r.ratio}|${r.adjustment_type}|${r.due_date}`).sort());


export default function ImportTemplateModalTwoTrack({ isDark, onClose, onImported }) {
  const c = isDark
    ? { card: '#1f2740', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', input: '#ffffff', inputBorder: '#c8ccd8' };
  const s = {
    btn: { padding: '9px 18px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',sans-serif" },
    btnGhost: { padding: '9px 16px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Public Sans',sans-serif" },
    th: { textAlign: 'left', padding: '6px 10px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', color: c.muted, borderBottom: `1px solid ${c.border}` },
    td: { padding: '6px 10px', fontSize: '13px', color: c.text, borderBottom: `1px solid ${c.border}` },
  };
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  // ── Parse both tabs → grouped assessors + collectors + installments, plus a dry-run against the DB ──
  async function handleFile(file) {
    if (!file) return;
    setBusy(true); setMsg(''); setPreview(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      // 1-2) Both tabs → assessors, collector pairings, installments, failures (utils/assessorTemplate).
      const { parsed, failures } = parseAssessorTemplate(wb);

      // 3) Group by assessor (find-or-create; rule 1: one assessor, N collectors, N links).
      const groupMap = new Map();
      for (const p of parsed) {
        const gk = `${p.state}|${nkey(p.assessorName)}`;
        let g = groupMap.get(gk);
        if (!g) { g = { state: p.state, assessorName: p.assessorName, assessorFields: p.assessorFields, collectors: [] }; groupMap.set(gk, g); }
        g.collectors.push({ name: p.collectorName, fields: p.collectorFields, installments: p.installments, valueYear: p.valueYear });
      }
      const groups = [...groupMap.values()];

      // Ambiguity: same (state, core) with differing data signatures across rows.
      const aB = new Map(), cB = new Map();
      for (const p of parsed) {
        const ak = `${p.state}|${nkey(p.assessorName)}`; if (!aB.has(ak)) aB.set(ak, { state: p.state, name: p.assessorName, sigs: new Set() }); aB.get(ak).sigs.add(sigOf(p.assessorFields));
        const ck = `${p.state}|${nkey(p.collectorName)}`; if (!cB.has(ck)) cB.set(ck, { state: p.state, name: p.collectorName, sigs: new Set() }); cB.get(ck).sigs.add(sigOf(p.collectorFields) + instSig(p.installments));
      }
      const ambiguous = [];
      for (const b of aB.values()) if (b.sigs.size > 1) ambiguous.push({ kind: 'assessor', state: b.state, name: b.name, variants: b.sigs.size });
      for (const b of cB.values()) if (b.sigs.size > 1) ambiguous.push({ kind: 'collector', state: b.state, name: b.name, variants: b.sigs.size });

      // 4) Per-state dry-run counts against the DB (distinct assessors/collectors; match by state+core).
      const states = [...new Set(parsed.map(p => p.state))];
      const stateStats = [];
      for (const st of states) {
        const [exA, exC] = await Promise.all([   // pageAll: page past the 1000-row cap so the existing-name set is complete (else dupes on sync)
          pageAll(() => supabase.from('assessors').select('id,name').eq('state', st)),
          pageAll(() => supabase.from('collectors').select('id,name').eq('state', st)),
        ]);
        const exAset = new Set((exA || []).map(a => nkey(a.name))), exCset = new Set((exC || []).map(cc => nkey(cc.name)));
        const aCore = new Map(), cCore = new Map(), pairs = new Set(); let rows = 0, re = 0, pp = 0;
        for (const p of parsed) {
          if (p.state !== st) continue; rows++;
          const ac = nkey(p.assessorName), cc = nkey(p.collectorName);
          if (!aCore.has(ac)) aCore.set(ac, exAset.has(ac));
          if (!cCore.has(cc)) { cCore.set(cc, exCset.has(cc)); re += p.installments.filter(x => x.kind === 'RE').length; pp += p.installments.filter(x => x.kind === 'PP').length; }
          pairs.add(`${ac}|${cc}`);
        }
        let aM = 0, aN = 0; for (const m of aCore.values()) m ? aM++ : aN++;
        let cM = 0, cN = 0; for (const m of cCore.values()) m ? cM++ : cN++;
        stateStats.push({ state: st, aMatched: aM, aNew: aN, cMatched: cM, cNew: cN, re, pp, links: pairs.size, dupes: rows - pairs.size, flagged: ambiguous.filter(a => a.state === st).length });
      }
      setPreview({ states: stateStats, failures, groups, ambiguous });
      setMsg(`Parsed ${parsed.length} collector rows → ${groups.length} assessors across ${states.length} state(s). Sync preview below — nothing is written until you click Import.`);
    } catch (e) {
      console.error('[import2] parse:', e); setMsg('Parse failed: ' + (e.message || e));
    } finally { setBusy(false); }
  }

  // ── Commit: SYNC (update-in-place, id-preserving) + clean-replace installments per (collector, year). ──
  async function commit() {
    if (!preview || busy) return;
    setBusy(true); setMsg('Importing…');
    try {
      const groups = preview.groups;
      const states = [...new Set(groups.map(g => g.state))];
      // ── VERSIONED SYNC: resolve/create the ENTITY by (state, nameCore), then UPSERT the (entity_id,
      //    value_year) row on the grain. A second year no longer overwrites the first — it inserts a new
      //    version (unique(entity_id, value_year)). This is the permanent clobber fix.
      const aEntity = new Map(), cEntity = new Map();   // (state|core) -> entity_id (shared across an entity's year-rows)
      const [ea, ec] = await Promise.all([
        loadEntitiesByStates('assessors', states),
        loadEntitiesByStates('collectors', states),
      ]);
      (ea || []).forEach(a => { const k = `${a.state}|${nkey(a.name)}`; if (!aEntity.has(k)) aEntity.set(k, a.entity_id); });
      (ec || []).forEach(x => { const k = `${x.state}|${nkey(x.name)}`; if (!cEntity.has(k)) cEntity.set(k, x.entity_id); });

      // unique entities in the sheet → { fields, valueYear } (collector year = the joined assessor's Value Year)
      const uA = new Map(), uC = new Map();
      for (const g of groups) {
        const ak = `${g.state}|${nkey(g.assessorName)}`; if (!uA.has(ak)) uA.set(ak, { fields: g.assessorFields, valueYear: g.assessorFields.value_year });
        for (const col of g.collectors) { const ck = `${col.fields.state}|${nkey(col.name)}`; if (!uC.has(ck)) uC.set(ck, { fields: col.fields, valueYear: col.valueYear }); }
      }

      // ⛔ PRE-FLIGHT, BEFORE THE FIRST WRITE. Everything below is unbatched — a failure at any point leaves
      // the preceding statements committed, and there is no rollback. So anything we can know up front must
      // be checked up front: every row must carry its own year, or it cannot be written one-year-at-a-time.
      {
        const aCheck = [...uA.values()].map(v => ({ value_year: v.valueYear }));
        const cCheck = [...uC.values()].map(v => ({ value_year: v.valueYear }));
        const err = preflightYears('assessor', aCheck, 'value_year') || preflightYears('collector', cCheck, 'value_year');
        if (err) { setMsg('Import aborted — ' + err); setBusy(false); return; }
      }

      // new entities → mint entity_id + insert the anchor; existing ones keep theirs (ids never churn).
      const anchorsA = [], anchorsC = [];
      for (const k of uA.keys()) { if (!aEntity.has(k)) { const eid = crypto.randomUUID(); aEntity.set(k, eid); anchorsA.push({ entity_id: eid }); } }
      for (const k of uC.keys()) { if (!cEntity.has(k)) { const eid = crypto.randomUUID(); cEntity.set(k, eid); anchorsC.push({ entity_id: eid }); } }
      for (const part of chunk(anchorsA, 500)) { const r = await supabase.from('assessor_entities').insert(part); if (r.error) throw r.error; }
      for (const part of chunk(anchorsC, 500)) { const r = await supabase.from('collector_entities').insert(part); if (r.error) throw r.error; }

      // upsert the year-rows on (entity_id, value_year). active OMITTED → new rows default true, re-imports keep retire state.
      // ⛔ CHUNK BY YEAR FIRST, THEN BY SIZE. The DB enforces cross-year isolation: one statement may touch at
      // most one value_year (the single-year statement triggers). A size-only chunk of a multi-year template
      // straddles years and is REJECTED — mid-batch, with earlier chunks already committed, because this loop
      // has no transaction. Grouping by year makes every statement single-year by construction, and it also
      // makes a failure legible ("2023 failed") instead of "chunk 7 of 19 failed".
      setMsg('Importing assessors…');
      const aRows = [...uA].map(([k, v]) => ({ ...v.fields, entity_id: aEntity.get(k), value_year: v.valueYear }));
      for (const [, part] of byYearChunks(aRows, 'value_year', 500)) {
        const r = await supabase.from('assessors').upsert(part, { onConflict: 'entity_id,value_year' }); if (r.error) throw r.error;
      }

      setMsg('Importing collectors…');
      const cRows = [...uC].map(([k, v]) => ({ ...v.fields, entity_id: cEntity.get(k), value_year: v.valueYear }));
      for (const [, part] of byYearChunks(cRows, 'value_year', 500)) {
        const r = await supabase.from('collectors').upsert(part, { onConflict: 'entity_id,value_year' }); if (r.error) throw r.error;
      }

      setMsg('Linking…');
      const seen = new Set(), links = [];
      for (const g of groups) {
        const aid = aEntity.get(`${g.state}|${nkey(g.assessorName)}`); if (!aid) continue;
        for (const col of g.collectors) { const cid = cEntity.get(`${col.fields.state}|${nkey(col.name)}`); if (!cid) continue; const lk = `${aid}|${cid}`; if (seen.has(lk)) continue; seen.add(lk); links.push({ assessor_id: aid, collector_id: cid }); }
      }
      for (const part of chunk(links, 500)) { const l = await supabase.from('assessor_collectors').upsert(part, { onConflict: 'assessor_id,collector_id', ignoreDuplicates: true }); if (l.error) throw l.error; }

      // Installments — clean-replace per (collector ENTITY, tax_year) [rule 9], then bulk insert.
      setMsg('Importing installments…');
      const instRows = [], byYear = new Map(), instSeen = new Set();
      for (const g of groups) {
        for (const col of g.collectors) {
          const cid = cEntity.get(`${col.fields.state}|${nkey(col.name)}`);
          if (!cid || !col.installments.length || col.valueYear == null) continue;
          const dk = `${cid}|${col.valueYear}`; if (instSeen.has(dk)) continue; instSeen.add(dk);
          if (!byYear.has(col.valueYear)) byYear.set(col.valueYear, new Set()); byYear.get(col.valueYear).add(cid);
          col.installments.forEach(r => instRows.push({
            collector_id: cid, tax_year: col.valueYear, kind: r.kind, installment_number: r.installment_number,
            bill_type: r.bill_type, ratio: r.ratio, split_pct: r.split_pct, tax_type: r.tax_type,
            adjustment_type: r.adjustment_type, adjustment_pct: r.adjustment_pct, due_date: r.due_date,
          }));
        }
      }
      for (const [year, cidSet] of byYear) { for (const part of chunk([...cidSet], 100)) { const d = await supabase.from('collector_installments').delete().eq('tax_year', year).in('collector_id', part); if (d.error) throw d.error; } }
      for (const [, part] of byYearChunks(instRows, 'tax_year', 500)) { const ins = await supabase.from('collector_installments').insert(part); if (ins.error) throw ins.error; }

      setMsg(`Import complete — ${uA.size} assessors, ${uC.size} collectors, ${links.length} links, ${instRows.length} installments.`);
      if (onImported) onImported();
    } catch (e) {
      console.error('[import2] commit:', e); setMsg('Import failed: ' + (e.message || e));
    } finally { setBusy(false); }
  }

  const hardFails = preview ? preview.failures.filter(f => !f.soft && !f.drop) : [];
  const softFails = preview ? preview.failures.filter(f => f.soft) : [];
  const dropFails = preview ? preview.failures.filter(f => f.drop) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '14px', width: '100%', maxWidth: '860px', padding: '24px 26px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>Import Assessor/Collector Template (two-track)</div>
          <button style={s.btnGhost} onClick={onClose} disabled={busy}>✕</button>
        </div>
        <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Reads the "Assessors Screen" + "Collectors" tabs. A repeated assessor becomes one assessor with multiple collectors. Sync import — matched rows updated in place (ids preserved), new ones inserted, nothing deleted; installments refresh per (collector, year). Dry-run below — nothing is written until you click Import.</div>

        <label style={{ ...s.btnGhost, display: 'inline-block', cursor: 'pointer' }}>
          {fileName ? fileName : 'Choose Template (.xlsx)'}
          <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} disabled={busy} onChange={e => { const f = e.target.files[0]; if (f) { setFileName(f.name); handleFile(f); } }} />
        </label>
        {msg && <div style={{ fontSize: '12px', color: c.sub, marginTop: '10px' }}>{msg}</div>}

        {preview && (
          <div style={{ marginTop: '18px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
              <thead><tr>
                <th style={s.th}>State</th><th style={s.th}>Assessors</th><th style={s.th}>Collectors</th><th style={s.th}>Installments (RE/PP)</th><th style={s.th}>Links</th><th style={s.th}>Dupes</th><th style={s.th}>Flagged</th>
              </tr></thead>
              <tbody>
                {preview.states.map(st => (
                  <tr key={st.state}>
                    <td style={s.td}><b>{st.state}</b> <span style={{ color: c.muted }}>({stateName(st.state)})</span></td>
                    <td style={s.td}>{st.aMatched + st.aNew} <span style={{ color: c.muted }}>({st.aMatched}m / {st.aNew}n)</span></td>
                    <td style={s.td}>{st.cMatched + st.cNew} <span style={{ color: c.muted }}>({st.cMatched}m / {st.cNew}n)</span></td>
                    <td style={s.td}>{st.re + st.pp} <span style={{ color: c.muted }}>({st.re} RE / {st.pp} PP)</span></td>
                    <td style={s.td}>{st.links}</td><td style={s.td}>{st.dupes || 0}</td>
                    <td style={s.td}>{st.flagged ? <span style={{ color: 'var(--red)', fontWeight: '700' }}>{st.flagged}</span> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.ambiguous.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--red)', marginBottom: '4px' }}>⚠ {preview.ambiguous.length} ambiguous name(s) — same core, different data (first used — verify):</div>
                <div style={{ maxHeight: '110px', overflowY: 'auto', fontSize: '12px', color: c.sub }}>{preview.ambiguous.slice(0, 50).map((a, i) => <div key={i}>{a.state} {a.kind}: "{a.name}" — {a.variants} variants</div>)}</div>
              </div>
            )}
            {hardFails.length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--red)', marginBottom: '4px' }}>⚠ {hardFails.length} row(s) SKIPPED:</div>
                <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px', color: c.sub }}>{hardFails.slice(0, 50).map((f, i) => <div key={i}>Row {f.rowNo}: {f.reason}</div>)}</div>
              </div>
            )}
            {dropFails.length > 0 && (
              <div style={{ marginBottom: '10px', padding: '8px 10px', border: '1px solid var(--red)', borderRadius: '8px', background: isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.06)' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--red)', marginBottom: '4px' }}>⚠ {dropFails.length} installment(s) DROPPED — unparseable due date. Fix the sheet, re-import:</div>
                <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px', color: c.sub }}>{dropFails.slice(0, 100).map((f, i) => <div key={i}>Row {f.rowNo}: {f.reason}</div>)}</div>
              </div>
            )}
            {softFails.length > 0 && <div style={{ fontSize: '12px', color: isDark ? '#fbbf24' : '#8A5B00', marginBottom: '10px' }}>{softFails.length} collector(s) imported with NO installments (see console).</div>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button style={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
              <button style={busy ? { ...s.btn, opacity: 0.5 } : s.btn} onClick={commit} disabled={busy}>{busy ? 'Importing…' : `Import ${preview.groups.reduce((n, g) => n + g.collectors.length, 0)} collector rows`}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
